import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Optional,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    AirspaceCheckDto,
    AirspaceJurisdictionDto,
    RuleOutcome,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { collapseAnimation } from '@flyfreely-portal-ui/ui';
import { AirspaceCheckService } from 'libs/airspace/src/lib/airspace-check/airspace-check.service';
import { AirspaceDialoguesService } from 'libs/airspace/src/lib/airspace-dialogues.service';
import { FullScreenService } from 'libs/fullscreen/src/lib/fullscreen.service';
import moment from 'moment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import {
    AirspaceCheckerParameters,
    MapAirspaceCheckerService
} from './map-airspace-checker.service';

export interface InitialCheckerParameters {
    date: Date;
    duration: number;
    rulesetIdentifier: string;
    height: number;
    timeZone: string;
}

export interface AirspaceCheckerParametersWithStartEnd
    extends AirspaceCheckerParameters {
    startTime: string;
    endTime: string;
}

/**
 * This component provides the airspace checker that sits in the map view, including on the
 * dashboard.
 * There are 3 possible modes: draw w/ checker, draw w/o checker, checker only.
 * Internally, there can be 2 jurisdictions. The first provided by the parent component, which should
 * drive which
 */
@Component({
    selector: 'map-airspace-checker',
    templateUrl: './map-airspace-checker.component.html',
    styleUrls: ['./map-airspace-checker.component.scss'],
    animations: [collapseAnimation()]
})
export class MapAirspaceChecker {
    @Input() organisationId: number;
    @Input() timeZone: string;
    @Input() flightArea: GeoJSON.Polygon | GeoJSON.Point | null;
    @Input() initialValues: InitialCheckerParameters;
    @Input() workTracker: WorkTracker;
    @Input() jurisdiction?: AirspaceJurisdictionDto;
    @Input() radius: number;
    @Input() emptyStateMessage = 'Draw a flight area to begin';
    @Input() allowMissionCreation = false;
    @Input() showJurisdictionLinks = true;
    /**
     * Should the parameters panel be disabled
     */
    @Input() disableParameters = false;
    /**
     * Can the user draw their own checker area.
     */
    @Input() allowDrawing = false;
    @Input() dashboardCheckerMode: 'MAP' | 'BOTH' | 'NONE' = 'NONE';

    /**
     * The checker parameters have been updated
     */
    @Output() checkerParametersChange =
        new EventEmitter<AirspaceCheckerParametersWithStartEnd>();

    /**
     * The create mission button was pressed
     */
    @Output() createMission = new EventEmitter<void>();

    /**
     * The checker is awaiting a feature to check
     */
    @Output() awaitingFeature = new EventEmitter<boolean>();
    @Output() requestToggleFullscreen = new EventEmitter<void>();

    /**
     * Request that the flightArea input be set to null
     */
    @Output() clearFlightArea = new EventEmitter<void>();
    @Output() hasCheckResult = new EventEmitter<boolean>();

    checkerTimeZone: string;

    useSimplified = false;
    showSimplifiedOutcome = false;
    editingAirspaceParameters = false;

    firstCheck = true;
    showCheckResult = false;

    /**
     * The text of the authorisations available
     */
    authorisationAvailable: string = null;

    checkerTimeZoneAbbreviation: string;

    airspaceStatus:
        | 'CAN_FLY'
        | 'CAN_FLY_CONDITIONS'
        | 'CANNOT_FLY'
        | 'INCOMPLETE'
        | 'CHECKING'
        | 'IDLE' = 'IDLE';

    // This indicates that we are currently drawing a location to check. This is separate the airspaceStatus so we
    // can revert when the check is cancelled
    isDrawing = false;

    caaAuthorisationStatus:
        | 'AUTHORISATION_REQUIRED'
        | 'AUTHORISATION_NOT_REQUIRED'
        | 'UNKNOWN';
    airspaceResultCounter: number;
    hasFlightArea: boolean;

    /**
     * Is the airspace checker supported or not. If not we are in drawing only mode.
     */
    canCheckAirspace = false;

    working: boolean;
    private ngUnsubscribe$ = new Subject<void>();

    airspaceInitialised$: Observable<boolean>;

    constructor(
        private airspaceCheckService: AirspaceCheckService,
        public mapAirspaceCheckerService: MapAirspaceCheckerService,
        private deviceDetectorService: DeviceDetectorService,
        private airspaceDialoguesService: AirspaceDialoguesService,
        private changeDetector: ChangeDetectorRef,
        // The full screen service is provided by a grandparent component and won't always be available
        @Optional() private fullscreenService: FullScreenService
    ) {
        // Initialised once we have any parameters
        this.airspaceInitialised$ =
            mapAirspaceCheckerService.currentParameters$.pipe(map(() => true));
    }

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.airspaceCheckService.resultStatus$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(status => {
                if (
                    !this.hasFlightArea &&
                    !this.firstCheck &&
                    this.canCheckAirspace
                ) {
                    this.airspaceStatus = 'INCOMPLETE';
                    this.caaAuthorisationStatus = 'UNKNOWN';
                    this.hasCheckResult.emit(false);
                    return;
                }
                if (status === 'CHECKING' || status === 'INCOMPLETE') {
                    this.showSimplifiedOutcome = this.useSimplified;
                    this.hasCheckResult.emit(false);
                }
                switch (status) {
                    case 'CAN_FLY':
                        this.airspaceStatus = 'CAN_FLY';
                        this.hasCheckResult.emit(true);
                        break;

                    case 'CAN_FLY_CONDITIONS':
                        this.airspaceStatus = 'CAN_FLY_CONDITIONS';
                        this.hasCheckResult.emit(true);
                        break;

                    case 'CANNOT_FLY':
                        this.airspaceStatus = 'CANNOT_FLY';
                        this.hasCheckResult.emit(true);
                        break;

                    case 'CHECKING':
                        this.airspaceStatus = 'CHECKING';
                        this.caaAuthorisationStatus = 'UNKNOWN';
                        this.hasCheckResult.emit(false);
                        break;

                    case 'INCOMPLETE':
                    case 'NO_CHECK':
                        this.airspaceStatus =
                            this.firstCheck && status === 'INCOMPLETE'
                                ? 'IDLE'
                                : 'INCOMPLETE';
                        this.caaAuthorisationStatus = 'UNKNOWN';
                        this.hasCheckResult.emit(false);
                        break;

                    default:
                        this.airspaceStatus = 'IDLE';
                        this.caaAuthorisationStatus = 'UNKNOWN';
                        this.hasCheckResult.emit(false);
                        break;
                }
                // Automatically expand the results on the first check if not on mobile
                if (
                    this.firstCheck &&
                    !(
                        this.deviceDetectorService.isMobile() ||
                        (this.deviceDetectorService.isTablet() &&
                            this.deviceDetectorService.orientation.toLowerCase() ===
                                'portrait')
                    )
                ) {
                    if (
                        status === 'CAN_FLY' ||
                        status === 'CANNOT_FLY' ||
                        status === 'CAN_FLY_CONDITIONS'
                    ) {
                        this.firstCheck = false;
                        this.showCheckResult = true;
                    }
                }
            });

        this.airspaceCheckService.result$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(result => {
                if (result == null) {
                    this.airspaceStatus = this.firstCheck
                        ? 'IDLE'
                        : 'INCOMPLETE';
                    this.showCheckResult = false;
                    this.showSimplifiedOutcome =
                        this.useSimplified &&
                        this.airspaceStatus === 'INCOMPLETE';
                    this.caaAuthorisationStatus = 'UNKNOWN';
                    this.authorisationAvailable = null;
                    return;
                }

                this.authorisationAvailable =
                    result.availableAutomatedAuthorisationList.length > 0
                        ? 'Authorizations available'
                        : null;
                const status = result.flightStatus;
                if (!this.useSimplified) {
                    const s = AirspaceCheckDto.FlightStatus;
                    switch (status) {
                        case s.CAN_FLY:
                            this.airspaceResultCounter =
                                result.ruleOutcomes.filter(
                                    o => o.outcome === RuleOutcome.Outcome.PASS
                                ).length;
                            break;

                        case s.CAN_FLY_CONDITIONS:
                            this.airspaceResultCounter =
                                result.ruleOutcomes.filter(
                                    o =>
                                        o.outcome ===
                                            RuleOutcome.Outcome.ADVISE ||
                                        o.outcome ===
                                            RuleOutcome.Outcome
                                                .AUTHORISATION_REQUIRED
                                ).length;
                            break;

                        case s.CANNOT_FLY:
                            this.airspaceResultCounter =
                                result.ruleOutcomes.filter(
                                    o => o.outcome === RuleOutcome.Outcome.BLOCK
                                ).length;
                            break;

                        default:
                            this.airspaceResultCounter = 0;
                            break;
                    }
                } else {
                    if (
                        result.availableAutomatedAuthorisationList != null &&
                        result.availableAutomatedAuthorisationList.length > 0
                    ) {
                        if (
                            result.ruleOutcomes.find(
                                o =>
                                    o.outcome != null &&
                                    o.outcome === 'AUTHORISATION_REQUIRED'
                            ) != null
                        ) {
                            this.caaAuthorisationStatus =
                                'AUTHORISATION_REQUIRED';
                        } else {
                            this.caaAuthorisationStatus =
                                'AUTHORISATION_NOT_REQUIRED';
                        }
                    } else {
                        this.caaAuthorisationStatus = 'UNKNOWN';
                    }
                }
                this.changeDetector.markForCheck();
            });

        this.mapAirspaceCheckerService.airspaceJurisdiction$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(jurisdiction => {
                if (jurisdiction == null || jurisdiction.identifier == null) {
                    this.canCheckAirspace = false;
                    this.showCheckResult = false;
                    return;
                }

                this.firstCheck = true;
                this.clearFlightArea.emit();

                this.useSimplified =
                    jurisdiction.airspaceCheckSupport === 'AUTHORISATION_CHECK';
                this.canCheckAirspace =
                    jurisdiction.airspaceCheckSupport === 'AIRSPACE_CHECK';
            });

        this.mapAirspaceCheckerService.currentParameters$
            .pipe(
                filter(
                    p => p.airspaceDate != null && p.airspaceDuration != null
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(parameters => {
                this.checkerParametersChange.emit({
                    airspaceDate: parameters.airspaceDate!,
                    airspaceDuration: parameters.airspaceDuration!,
                    airspaceHeight: parameters.airspaceHeight!,
                    airspaceRuleset: parameters.airspaceRuleset!,
                    airspaceTimeZone: parameters.airspaceTimeZone!,
                    radius: parameters.radius,
                    startTime: parameters.airspaceDate?.toISOString(),
                    endTime: moment(parameters.airspaceDate)
                        .add(parameters.airspaceDuration, 's')
                        .toISOString()
                });
            });

        this.mapAirspaceCheckerService.setup(
            this.organisationId,
            {
                airspaceDate: this.initialValues?.date,
                airspaceDuration: this.initialValues?.duration,
                airspaceHeight: this.initialValues?.height,
                airspaceTimeZone: this.initialValues?.timeZone
            },
            { rulesetIdentifier: this.initialValues?.rulesetIdentifier }
        );
    }

    ngOnDestroy() {
        // Ensure the checker results don't persist for this checker  after closing the modal
        this.resetCheckResults();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('flightArea' in changes) {
            this.hasFlightArea = this.flightArea != null;

            this.mapAirspaceCheckerService.setFlightArea(this.flightArea);
            this.stopDrawing();
        }
        if ('timeZone' in changes) {
            this.mapAirspaceCheckerService.setTimeZone(this.timeZone);
        }
        if ('jurisdiction' in changes) {
            this.mapAirspaceCheckerService.setJurisdiction(this.jurisdiction);
        }
    }

    airspaceCheckClick() {
        if (
            this.allowDrawing &&
            ((this.airspaceStatus === 'IDLE' &&
                this.firstCheck &&
                this.canCheckAirspace) ||
                !this.canCheckAirspace)
        ) {
            this.startDrawing();
            return;
        }

        if (
            this.canCheckAirspace &&
            this.airspaceStatus !== 'CHECKING' &&
            this.airspaceStatus !== 'IDLE'
        ) {
            this.showCheckResult = !this.showCheckResult;
        } else {
            this.showCheckResult = false;
        }
    }

    startDrawing() {
        this.isDrawing = true;
        this.awaitingFeature.next(this.isDrawing);
    }

    stopDrawing() {
        this.isDrawing = false;
        this.awaitingFeature.next(this.isDrawing);
    }

    private resetCheckResults() {
        if (
            this.airspaceStatus !== 'INCOMPLETE' &&
            this.airspaceStatus !== 'IDLE' &&
            this.airspaceResultCounter > 0
        ) {
            this.airspaceCheckService.resetCheckResults();
        }
    }

    checkIfFullscreenAndExit() {
        if (
            this.fullscreenService != null &&
            this.fullscreenService.isFullScreen()
        ) {
            this.requestToggleFullscreen.emit();
        }
    }

    showAirspaceCheckDetails() {
        this.checkIfFullscreenAndExit();
        this.airspaceDialoguesService.showAirspaceCheckDetailsDialogue(
            this.airspaceCheckService
        );
    }

    requestCreateMission() {
        this.checkIfFullscreenAndExit();
        this.createMission.emit();
    }

    showParameterEditor() {
        this.editingAirspaceParameters = !this.disableParameters;
    }

    hideParameterEditor() {
        this.editingAirspaceParameters = false;
    }
}
